// components/CalculatorForm.js
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, TextField, Button, Card, CardContent,
  Box, CircularProgress, Alert, Snackbar, FormControl, InputLabel, Select,
  MenuItem, Chip, Tooltip
} from '@mui/material';
import {
  Business, AccessTime, AttachMoney, Calculate, PictureAsPdf, Save, Cancel,
  DateRange, DragIndicator, SwapVert, CardGiftcard, Settings
} from '@mui/icons-material';
import CompanyInfoSection from './CompanyInfoSection';
import ResultsSection from './ResultsSection';
import FinalAmountAdjuster from './FinalAmountAdjuster';
import SummarySection from './SummarySection';
import { axiosInstance } from '../../utils/axios';
import { generatePDF } from '../../utils/pdfGenerator';
import { TIPO_OPTIONS } from '../clients/parametros';

const IVA_RATE = 0.21;
const BONO_BASE_RATE = 937.5;

const MESES_DISPONIBLES = [
  { key: 'ene', label: 'Enero', order: 1 },
  { key: 'feb', label: 'Febrero', order: 2 },
  { key: 'mar', label: 'Marzo', order: 3 },
  { key: 'abr', label: 'Abril', order: 4 },
  { key: 'may', label: 'Mayo', order: 5 },
  { key: 'jun', label: 'Junio', order: 6 },
  { key: 'jul', label: 'Julio', order: 7 },
  { key: 'ago', label: 'Agosto', order: 8 },
  { key: 'sep', label: 'Septiembre', order: 9 },
  { key: 'oct', label: 'Octubre', order: 10 },
  { key: 'nov', label: 'Noviembre', order: 11 },
  { key: 'dic', label: 'Diciembre', order: 12 }
];

const TIPOS_CLIENTE = TIPO_OPTIONS;

const getTipoClienteNumerico = (tipoNumerico) => {
  const tipoInt = parseInt(tipoNumerico);
  const tipo = TIPO_OPTIONS.find(t => t.value === tipoInt);
  return tipo ? tipo.value : 0;
};

function generarComunicado(tipoCliente, nombreCliente, aumentoGeneral, bonoNavideno, costoEmpresarial, fecha) {
  let incrementoAdicional = 0;
  let detalleAmbito = "";
  const tipoClienteNum = parseInt(tipoCliente);
  
  switch (tipoClienteNum) {
    case 0:
      detalleAmbito = "cielos abiertos, en el que se realicen tareas de limpieza, mantenimiento y/o desmalezamiento, jardinería tareas especiales";
      incrementoAdicional = 15;
      break;
    case 1:
      detalleAmbito = "ámbito de salud, denominados, hospitales, sanatorios y clínicas";
      incrementoAdicional = 15;
      break;
    case 2:
      detalleAmbito = "propiedad horizontal y complejos de viviendas colectivas, tales como edificios, barrios cerrados, housing, country, condominio";
      incrementoAdicional = 10;
      break;
    default:
      detalleAmbito = "servicios generales";
  }

  return `Córdoba, ${fecha}

Cliente: "${nombreCliente}"
De nuestra mayor consideración:

Por medio del presente comunicado, me dirijo a usted para informar sobre la nueva convocatoria del gremio S.O.E.L.S.A.C. y la Cámara Cordobesa de Empresas de Servicios Generales (CA.SE.GE.), teniendo en cuenta el contexto inflacionario actual y la pérdida progresiva del poder adquisitivo de los trabajadores, el gremio decide abrir paritarias en carácter de urgente para solventar la situación económica de los trabajadores.

S.O.E.L.S.A.C. y la Cámara Cordobesa de Empresas de Servicios Generales (CA.SE.GE.) acuerdan un aumento del 14.53% del periodo septiembre 2025 a enero 2026 inclusive. Una suma bajo el concepto BONO NAVIDEÑO por única vez, por la suma de $165.000.- En los ámbitos de ${detalleAmbito}, fíjase un incremento adicional del ${incrementoAdicional}% sobre el sueldo básico.

El costo empresarial del aumento se ha determinado en un 15.75% al contabilizar las variables previsionales.`;
}

const CalculatorForm = ({ editingCalculation = null, onSaveComplete = null, onCancel = null }) => {
  const [formData, setFormData] = useState({
    nombreEmpresa: '', horasServicio: '', montoBase: '',
    porcentajeEne: '', porcentajeFeb: '', porcentajeMar: '', porcentajeAbr: '',
    porcentajeMay: '', porcentajeJun: '', porcentajeJul: '', porcentajeAgo: '',
    porcentajeSep: '', porcentajeOct: '', porcentajeNov: '', porcentajeDic: '',
    clienteId: null, tipoCliente: 0,
    clienteData: { nombre: '', horas: 0, monto: 0, iva: 0, tipoCliente: 0 }
  });

  const [mesesSeleccionados, setMesesSeleccionados] = useState(['sep', 'oct', 'nov', 'dic', 'ene']);
  const [ordenMeses, setOrdenMeses] = useState([9, 10, 11, 12, 1]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [calculatedData, setCalculatedData] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [montoFinalDeseado, setMontoFinalDeseado] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [savedCalculationId, setSavedCalculationId] = useState(null);
  const [savingCalculation, setSavingCalculation] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  function fechaFormateadaHoy() {
    const fecha = new Date();
    const opciones = { day: "2-digit", month: "long", year: "numeric" };
    const fechaFormateada = fecha.toLocaleDateString("es-AR", opciones);
    const partes = fechaFormateada.split(" de ");
    const mes = partes[1][0].toUpperCase() + partes[1].slice(1);
    return `${partes[0]} de ${mes} del ${partes[2]}`;
  }

  const calcularBonoNavideno = (horas) => BONO_BASE_RATE * (horas || 0);

  const convertirMontoSinIva = (monto, tieneIva) => {
    if (!tieneIva || tieneIva === 0) return monto;
    return monto / (1 + IVA_RATE);
  };

  const shouldShowIVA = () => formData.clienteData.iva === 1;

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newOrdenMeses = [...ordenMeses];
    const draggedItem = newOrdenMeses[draggedIndex];
    newOrdenMeses.splice(draggedIndex, 1);
    newOrdenMeses.splice(dropIndex, 0, draggedItem);
    setOrdenMeses(newOrdenMeses);
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    setTimeout(() => {
      calcular();
      showSnackbar('Orden actualizado y recalculado automáticamente.', 'success');
    }, 100);
  };

  const clearForm = () => {
    setIsLoadingData(true);
    setFormData(prev => ({
      nombreEmpresa: '', horasServicio: '', montoBase: '', clienteId: null, tipoCliente: 0,
      clienteData: { nombre: '', horas: 0, monto: 0, iva: 0, tipoCliente: 0 },
      porcentajeEne: prev.porcentajeEne, porcentajeFeb: prev.porcentajeFeb,
      porcentajeMar: prev.porcentajeMar, porcentajeAbr: prev.porcentajeAbr,
      porcentajeMay: prev.porcentajeMay, porcentajeJun: prev.porcentajeJun,
      porcentajeJul: prev.porcentajeJul, porcentajeAgo: prev.porcentajeAgo,
      porcentajeSep: prev.porcentajeSep, porcentajeOct: prev.porcentajeOct,
      porcentajeNov: prev.porcentajeNov, porcentajeDic: prev.porcentajeDic,
    }));
    setCalculatedData(null);
    setShowResults(false);
    setMontoFinalDeseado('');
    setSavedCalculationId(null);
    setTimeout(() => setIsLoadingData(false), 100);
  };

  const loadEditingData = (calculation) => {
    console.log('Loading editing data for calculation:', calculation);
    setIsLoadingData(true);
    const montoSinIva = convertirMontoSinIva(calculation.monto_base || 0, calculation.cliente_iva || 0);
    const tipoClienteNumerico = getTipoClienteNumerico(calculation.tipo);
    
    setFormData({
      nombreEmpresa: calculation.nombre_empresa || '',
      horasServicio: calculation.horas_servicio?.toString() || '',
      montoBase: montoSinIva.toString(),
      tipoCliente: tipoClienteNumerico,
      clienteData: {
        nombre: calculation.nombre_empresa || '',
        horas: calculation.horas_servicio || 0,
        monto: calculation.monto_base || 0,
        iva: calculation.cliente_iva || 0,
        tipoCliente: tipoClienteNumerico
      },
      porcentajeEne: calculation.porcentaje_ene?.toString() || '',
      porcentajeFeb: calculation.porcentaje_feb?.toString() || '',
      porcentajeMar: calculation.porcentaje_mar?.toString() || '',
      porcentajeAbr: calculation.porcentaje_abr?.toString() || '',
      porcentajeMay: calculation.porcentaje_may?.toString() || '',
      porcentajeJun: calculation.porcentaje_jun?.toString() || '',
      porcentajeJul: calculation.porcentaje_jul?.toString() || '',
      porcentajeAgo: calculation.porcentaje_ago?.toString() || '',
      porcentajeSep: calculation.porcentaje_sep?.toString() || '',
      porcentajeOct: calculation.porcentaje_oct?.toString() || '',
      porcentajeNov: calculation.porcentaje_nov?.toString() || '',
      porcentajeDic: calculation.porcentaje_dic?.toString() || '',
      clienteId: calculation.cliente_id
    });
    
    if (calculation.orden && Array.isArray(calculation.orden)) {
      setOrdenMeses(calculation.orden);
      const mesesFromOrden = calculation.orden.map(num => 
        MESES_DISPONIBLES.find(m => m.order === num)?.key
      ).filter(Boolean);
      setMesesSeleccionados(mesesFromOrden);
    } else if (typeof calculation.orden === 'string') {
      try {
        const ordenParsed = JSON.parse(calculation.orden);
        setOrdenMeses(ordenParsed);
        const mesesFromOrden = ordenParsed.map(num => 
          MESES_DISPONIBLES.find(m => m.order === num)?.key
        ).filter(Boolean);
        setMesesSeleccionados(mesesFromOrden);
      } catch (e) {
        console.error('Error parsing orden JSON:', e);
        loadMesesFromData(calculation);
      }
    } else {
      loadMesesFromData(calculation);
    }
    
    setSavedCalculationId(calculation.id);
    setTimeout(() => {
      setIsLoadingData(false);
      calcular();
    }, 200);
  };

  const loadMesesFromData = (calculation) => {
    const mesesActivos = [];
    const ordenActivo = [];
    MESES_DISPONIBLES.forEach(mes => {
      const valor = calculation[`porcentaje_${mes.key}`];
      if (valor && parseFloat(valor) > 0) {
        mesesActivos.push(mes.key);
        ordenActivo.push(mes.order);
      }
    });
    
    if (mesesActivos.length === 0) {
      setMesesSeleccionados(['sep', 'oct', 'nov', 'dic', 'ene']);
      setOrdenMeses([9, 10, 11, 12, 1]);
    } else {
      setMesesSeleccionados(mesesActivos);
      setOrdenMeses(ordenActivo);
    }
  };

  useEffect(() => {
    if (editingCalculation) {
      loadEditingData(editingCalculation);
    } else {
      clearForm();
    }
  }, [editingCalculation]);

  useEffect(() => {
    if (!isLoadingData && calculatedData && calculatedData.montoBase && mesesSeleccionados.length > 0) {
      calcular();
    }
  }, [ordenMeses]);

  const handleMesesChange = (event) => {
    const value = event.target.value;
    const newMesesSeleccionados = typeof value === 'string' ? value.split(',') : value;
    setMesesSeleccionados(newMesesSeleccionados);
    
    const newOrden = [];
    ordenMeses.forEach(num => {
      const mes = MESES_DISPONIBLES.find(m => m.order === num);
      if (mes && newMesesSeleccionados.includes(mes.key)) {
        newOrden.push(num);
      }
    });
    
    newMesesSeleccionados.forEach(mesKey => {
      const mes = MESES_DISPONIBLES.find(m => m.key === mesKey);
      if (mes && !newOrden.includes(mes.order)) {
        newOrden.push(mes.order);
      }
    });
    
    setOrdenMeses(newOrden);
    
    const newFormData = { ...formData };
    MESES_DISPONIBLES.forEach(mes => {
      if (!newMesesSeleccionados.includes(mes.key)) {
        newFormData[`porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = '';
      }
    });
    setFormData(newFormData);
  };

  const calcular = () => {
    const { nombreEmpresa, horasServicio, montoBase, tipoCliente, clienteData } = formData;
    const montoBaseNum = parseFloat(montoBase) || 0;
    const horasNum = parseFloat(horasServicio) || parseFloat(clienteData.horas) || 0;
    const bonoNavidenoSinIva = calcularBonoNavideno(horasNum);

    if (!montoBaseNum || montoBaseNum <= 0) {
      showSnackbar('Por favor ingrese un monto base válido mayor a 0', 'error');
      setCalculatedData(null);
      setShowResults(false);
      return;
    }

    if (mesesSeleccionados.length === 0) {
      showSnackbar('Por favor seleccione al menos un mes para calcular', 'error');
      setCalculatedData(null);
      setShowResults(false);
      return;
    }

    const mesesOrdenados = ordenMeses.map(num => 
      MESES_DISPONIBLES.find(m => m.order === num)
    ).filter(Boolean);

    const resultados = {};
    const porcentajesAcumulados = {};
    let porcentajeAcumulativo = 0;
    let montoFinal = montoBaseNum;

    mesesOrdenados.forEach((mes, index) => {
      const fieldName = `porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`;
      const porcentajeMes = parseFloat(formData[fieldName]) || 0;
      porcentajeAcumulativo += porcentajeMes;
      
      let montoMes = montoBaseNum * (1 + porcentajeAcumulativo / 100);
      let bonoPorMes = 0;
      if (mes.key === 'nov' && bonoNavidenoSinIva > 0) {
        bonoPorMes = bonoNavidenoSinIva;
      }
      
      resultados[`monto${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = montoMes;
      resultados[`porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = porcentajeMes;
      resultados[`bono${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = bonoPorMes;
      porcentajesAcumulados[`porcentajeAcum${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`] = porcentajeAcumulativo;
      montoFinal = montoMes;
    });

    const calculated = {
      nombreEmpresa, horasServicio: horasNum, montoBase: montoBaseNum, tipoCliente,
      bonoNavideno: bonoNavidenoSinIva, clienteData: formData.clienteData,
      showSinIva: !shouldShowIVA(), 
      showIVA: shouldShowIVA(),
      mesesSeleccionados, mesesOrdenados, ordenMeses,
      montoFinal, porcentajeTotal: porcentajeAcumulativo, clienteId: formData.clienteId,
      comunicado: generarComunicado(tipoCliente, nombreEmpresa, porcentajeAcumulativo.toFixed(1), 
        bonoNavidenoSinIva.toLocaleString('es-AR'), (porcentajeAcumulativo * 1.21).toFixed(1), fechaFormateadaHoy()),
      ...resultados, ...porcentajesAcumulados
    };

    setCalculatedData(calculated);
    setMontoFinalDeseado(montoFinal.toFixed(2));
    setShowResults(true);
    if (!editingCalculation) setSavedCalculationId(null);
    showSnackbar('Cálculo realizado exitosamente', 'success');
  };

  const saveCalculationToDatabase = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      throw new Error('No hay datos de cálculo para guardar');
    }
    if (!formData.clienteId) {
      throw new Error('Debe seleccionar un cliente');
    }

    try {
      const tipoParaGuardar = parseInt(formData.tipoCliente) || 0;
      
      const dataToSave = {
        cliente_id: formData.clienteId,
        nombre_empresa: calculatedData.nombreEmpresa,
        horas_servicio: calculatedData.horasServicio,
        monto_base: calculatedData.montoBase,
        tipo: tipoParaGuardar,
        bono_navideno: calculatedData.bonoNavideno,
        orden: JSON.stringify(ordenMeses),
        porcentaje_ene: parseFloat(formData.porcentajeEne) || 0,
        porcentaje_feb: parseFloat(formData.porcentajeFeb) || 0,
        porcentaje_mar: parseFloat(formData.porcentajeMar) || 0,
        porcentaje_abr: parseFloat(formData.porcentajeAbr) || 0,
        porcentaje_may: parseFloat(formData.porcentajeMay) || 0,
        porcentaje_jun: parseFloat(formData.porcentajeJun) || 0,
        porcentaje_jul: parseFloat(formData.porcentajeJul) || 0,
        porcentaje_ago: parseFloat(formData.porcentajeAgo) || 0,
        porcentaje_sep: parseFloat(formData.porcentajeSep) || 0,
        porcentaje_oct: parseFloat(formData.porcentajeOct) || 0,
        porcentaje_nov: parseFloat(formData.porcentajeNov) || 0,
        porcentaje_dic: parseFloat(formData.porcentajeDic) || 0
      };

      let response;
      if (editingCalculation && savedCalculationId) {
        response = await axiosInstance.put(`/calculo/${savedCalculationId}`, dataToSave);
        showSnackbar('Cálculo actualizado exitosamente', 'success');
      } else {
        response = await axiosInstance.post('/calculo', dataToSave);
        setSavedCalculationId(response.data.id);
        showSnackbar('Cálculo guardado exitosamente', 'success');
      }
      return response.data;
    } catch (error) {
      console.error('Error al guardar cálculo:', error);
      if (error.code === 'NETWORK_ERROR') {
        const localId = savedCalculationId || `local_${Date.now()}`;
        setSavedCalculationId(localId);
        showSnackbar('Cálculo guardado localmente (sin conexión a API)', 'warning');
        return { id: localId };
      }
      throw error;
    }
  };

  const handleSaveCalculation = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      showSnackbar('Por favor realice primero el cálculo antes de guardar', 'error');
      return;
    }
    if (!formData.clienteId) {
      showSnackbar('Debe seleccionar un cliente para guardar el cálculo', 'error');
      return;
    }

    setSavingCalculation(true);
    try {
      const savedData = await saveCalculationToDatabase();
      if (onSaveComplete) onSaveComplete(savedData);
      if (!editingCalculation) {
        setTimeout(() => {
          clearForm();
          showSnackbar('Formulario limpiado para nuevo cálculo', 'info');
        }, 2000);
      }
    } catch (error) {
      if (error.message.includes('cliente')) {
        showSnackbar('Error: Debe seleccionar un cliente válido', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Error: Datos inválidos. Verifique la información', 'error');
      } else {
        showSnackbar('Error al guardar el cálculo', 'error');
      }
    } finally {
      setSavingCalculation(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!calculatedData || !calculatedData.montoBase) {
      showSnackbar('Por favor realice primero el cálculo antes de generar el PDF', 'error');
      return;
    }
    if (!formData.clienteId) {
      showSnackbar('Debe seleccionar un cliente para generar el PDF', 'error');
      return;
    }

    setGeneratingPDF(true);
    try {
      showSnackbar('Guardando cálculo en la base de datos...', 'info');
      const savedData = await saveCalculationToDatabase();
      
      const pdfData = {
        ...calculatedData,
        nombreEmpresa: formData.nombreEmpresa || calculatedData.nombreEmpresa,
        clienteId: formData.clienteId,
        tipoCliente: parseInt(formData.tipoCliente) || 0,
        id: savedData.id
      };
      
      showSnackbar('Generando PDF...', 'info');
      await generatePDF(pdfData, () => {});
      showSnackbar(`PDF generado y cálculo guardado exitosamente (ID: ${savedData.id})`, 'success');
      if (onSaveComplete) onSaveComplete(savedData);
      if (!editingCalculation) {
        setTimeout(() => {
          clearForm();
          showSnackbar('Formulario limpiado para nuevo cálculo', 'info');
        }, 3000);
      }
    } catch (error) {
      console.error('Error en el proceso:', error);
      if (error.message.includes('cliente')) {
        showSnackbar('Error: Debe seleccionar un cliente válido', 'error');
      } else if (error.response?.status === 400) {
        showSnackbar('Error: Datos inválidos. Verifique la información', 'error');
      } else {
        try {
          await generatePDF(calculatedData, () => {});
          showSnackbar('PDF generado, pero no se pudo guardar en la base de datos', 'warning');
        } catch (pdfError) {
          showSnackbar('Error al generar el PDF', 'error');
        }
      }
    } finally {
      setGeneratingPDF(false);
    }
  };

  const ajustarMontoFinal = () => {
    const montoFinalDeseadoNum = parseFloat(montoFinalDeseado);
    if (!montoFinalDeseadoNum || montoFinalDeseadoNum <= calculatedData.montoBase) {
      showSnackbar('Por favor ingrese un monto final válido (mayor al monto base)', 'error');
      return;
    }

    const nuevoPorcentajeTotal = ((montoFinalDeseadoNum - calculatedData.montoBase) / calculatedData.montoBase) * 100;
    const porcentajeOriginalTotal = calculatedData.porcentajeTotal;
    if (porcentajeOriginalTotal === 0) {
      showSnackbar('No hay porcentajes configurados para ajustar', 'error');
      return;
    }
    
    const factor = nuevoPorcentajeTotal / porcentajeOriginalTotal;
    const newFormData = { ...formData };
    mesesSeleccionados.forEach(mesKey => {
      const fieldName = `porcentaje${mesKey.charAt(0).toUpperCase() + mesKey.slice(1)}`;
      const valorActual = parseFloat(formData[fieldName]) || 0;
      const nuevoValor = valorActual * factor;
      newFormData[fieldName] = nuevoValor.toFixed(2);
    });

    setFormData(newFormData);
    showSnackbar('Porcentajes ajustados. Recalcule para ver los nuevos resultados.', 'info');
  };

  const isEditMode = !!editingCalculation;

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {isEditMode ? 'Editar Cálculo' : 'Calculadora de Aumentos Progresivos'}
          </Typography>
          {isEditMode && onCancel && (
            <Button variant="outlined" startIcon={<Cancel />} onClick={onCancel} color="inherit">
              Cancelar Edición
            </Button>
          )}
        </Box>

        {isEditMode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Editando cálculo ID: {savedCalculationId}. Los cambios se guardarán cuando presione "Guardar" o "Generar PDF".
          </Alert>
        )}

        {formData.clienteId && formData.clienteData.nombre && (
          <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business />Información del Cliente
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Cliente</Typography>
                  <Typography variant="h6">{formData.clienteData.nombre}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Horas de Servicio</Typography>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime />{formData.clienteData.horas}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Monto Base {formData.clienteData.iva ? '(Con IVA convertido a Sin IVA)' : '(Sin IVA)'}
                  </Typography>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney />${parseFloat(formData.montoBase || 0).toLocaleString('es-AR')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Tipo de Cliente</Typography>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {TIPOS_CLIENTE.find(t => t.value === formData.clienteData.tipoCliente)?.icon || <Business />}
                    {TIPOS_CLIENTE.find(t => t.value === formData.clienteData.tipoCliente)?.label || formData.clienteData.tipoCliente}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Bono Navideño Automático:</strong> ${calcularBonoNavideno(formData.clienteData.horas).toLocaleString('es-AR')} 
                (${BONO_BASE_RATE} × {formData.clienteData.horas} horas)
                {shouldShowIVA() ? ' + IVA en columna "Con IVA"' : ''}
              </Typography>
            </Alert>

            {shouldShowIVA() ? 
              <Alert severity="warning" sx={{ mt: 1 }}>
                Este cliente maneja precios con IVA. Los cálculos se realizan con el monto convertido a Sin IVA y el PDF incluirá la columna "Con IVA".
              </Alert> :              
              <Alert severity="primary" sx={{ mt: 1 }}>
                Este cliente maneja precios sin IVA. El PDF no incluirá la columna "Con IVA".
              </Alert>
            }

            {formData.clienteData.tipoCliente !== '' && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <strong>Tipo de Cliente:</strong> {TIPOS_CLIENTE.find(t => t.value === formData.clienteData.tipoCliente)?.label || formData.clienteData.tipoCliente}
                <br />
                <Typography variant="caption">
                  {TIPOS_CLIENTE.find(t => t.value === formData.clienteData.tipoCliente)?.descripcion}
                </Typography>
              </Alert>
            )}
          </Paper>
        )}
        
        <CompanyInfoSection formData={formData} setFormData={setFormData} />

        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateRange />Seleccionar Meses para Aumentos
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Meses a incluir en el cálculo</InputLabel>
            <Select
              label="Meses a incluir en el cálculo"
              multiple
              value={mesesSeleccionados}
              onChange={handleMesesChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const mes = MESES_DISPONIBLES.find(m => m.key === value);
                    return <Chip key={value} label={mes?.label || value} size="small" color="primary" />;
                  })}
                </Box>
              )}
            >
              {MESES_DISPONIBLES.map((mes) => (
                <MenuItem key={mes.key} value={mes.key}>
                  {mes.label}
                  {mes.key === 'nov' && formData.clienteData.horas > 0 && (
                    <Chip 
                      label={`+Bono ${calcularBonoNavideno(formData.clienteData.horas).toLocaleString('es-AR')}`}
                      size="small" color="secondary" sx={{ ml: 1 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Seleccione los meses donde desea aplicar aumentos. Podrá reordenarlos arrastrando y soltando.
          </Typography>
        </Paper>

        <Card sx={{ mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Como funcionan los aumentos progresivos</Typography>
            <Typography variant="body2" paragraph>
              <strong>Los porcentajes se acumulan según el orden que usted defina:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              Puede personalizar completamente el orden de aplicación de los aumentos usando drag & drop.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Ejemplo: Si configura Dic-Ene-Feb con 10% cada mes, en febrero tendrá un 30% de aumento total.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'warning.dark' }}>
              Nota: El bono navideño se calcula automáticamente (${BONO_BASE_RATE} × horas) y se suma únicamente en noviembre.
            </Typography>
          </CardContent>
        </Card>

        {mesesSeleccionados.length > 0 && (
          <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">Porcentajes de Aumento por Mes</Typography>
              <Tooltip title="Arrastra y suelta para reordenar"><SwapVert color="action" /></Tooltip>
            </Box>
            
            <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
              Orden de aplicación: {ordenMeses.map((num, index) => {
                const mes = MESES_DISPONIBLES.find(m => m.order === num);
                return `${index + 1}. ${mes?.label || num}`;
              }).join(' → ')}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Tip:</strong> Arrastra los campos por el ícono de líneas para cambiar el orden de aplicación de los aumentos
            </Alert>
            
            <Grid container spacing={3}>
              {ordenMeses
                .map(num => MESES_DISPONIBLES.find(m => m.order === num))
                .filter(Boolean)
                .map((mes, index) => {
                  const fieldName = `porcentaje${mes.key.charAt(0).toUpperCase() + mes.key.slice(1)}`;
                  const isDraggedOver = dragOverIndex === index;
                  const isBeingDragged = draggedIndex === index;
                  const isNoviembre = mes.key === 'nov';
                  const bonoMonto = calcularBonoNavideno(formData.clienteData.horas);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={`${mes.key}-${index}`}>
                      <Card
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        sx={{
                          cursor: 'grab',
                          transition: 'all 0.2s ease',
                          transform: isBeingDragged ? 'rotate(5deg) scale(0.95)' : 'none',
                          boxShadow: isDraggedOver ? 4 : 1,
                          border: isDraggedOver ? '2px dashed #1976d2' : '1px solid transparent',
                          backgroundColor: isDraggedOver ? 'action.hover' : 'background.paper',
                          '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
                          '&:active': { cursor: 'grabbing' }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <DragIndicator sx={{ color: 'action.active', cursor: 'grab' }} />
                            <Typography variant="h6" sx={{ 
                              fontSize: '1rem', fontWeight: 600,
                              color: index === 0 ? 'primary.main' : 'text.primary'
                            }}>
                              {index + 1}. {mes.label}
                            </Typography>
                            {isNoviembre && bonoMonto > 0 && <CardGiftcard color="secondary" />}
                          </Box>
                          
                          <TextField
                            fullWidth
                            label={`Porcentaje de aumento`}
                            type="number"
                            value={formData[fieldName]}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [fieldName]: e.target.value
                            }))}
                            InputProps={{ endAdornment: '%' }}
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText={`Se aplicará en posición ${index + 1}`}
                            sx={{
                              '& .MuiInputLabel-root': {
                                color: index === 0 ? 'primary.main' : 'text.secondary'
                              }
                            }}
                          />
                          
                          {isNoviembre && bonoMonto > 0 && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                              <Typography variant="caption">
                                + Bono Navideño: ${bonoMonto.toLocaleString('es-AR')} (Sin IVA)
                                {shouldShowIVA() && (
                                  <>
                                    <br />Con IVA: ${(bonoMonto * (1 + IVA_RATE)).toLocaleString('es-AR')}
                                  </>
                                )}
                              </Typography>
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Los porcentajes se aplicarán en el orden mostrado arriba. Use drag & drop para reordenar fácilmente.
            </Typography>
          </Paper>
        )}

        <Button
          variant="contained"
          size="large"
          startIcon={<Calculate />}
          onClick={calcular}
          sx={{ mt: 3 }}
          disabled={mesesSeleccionados.length === 0}
        >
          {isEditMode ? 'Recalcular' : 'Calcular Aumentos'}
        </Button>
      </Paper>

      {showResults && calculatedData && (
        <>
          <ResultsSection 
            calculatedData={calculatedData} 
            showIVA={calculatedData.showIVA}
            setShowIVA={() => {}}
            IVA_RATE={IVA_RATE}
          />

          <FinalAmountAdjuster 
            montoFinalDeseado={montoFinalDeseado}
            setMontoFinalDeseado={setMontoFinalDeseado}
            ajustarMontoFinal={ajustarMontoFinal}
          />

          <SummarySection calculatedData={calculatedData} IVA_RATE={IVA_RATE} />

          {calculatedData.comunicado && (
            <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business />Vista Previa del Comunicado
              </Typography>
              <Box sx={{ 
                bgcolor: 'grey.50', p: 2, border: '1px solid', borderColor: 'grey.300',
                borderRadius: 1, maxHeight: 400, overflow: 'auto'
              }}>
                <Typography variant="body2" component="pre" sx={{ 
                  whiteSpace: 'pre-wrap', fontFamily: 'monospace',
                  fontSize: '0.85rem', lineHeight: 1.4
                }}>
                  {calculatedData.comunicado}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Este texto se incluirá en el PDF generado
              </Typography>
            </Paper>
          )}

          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings />Configuración Automática del PDF
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {shouldShowIVA() 
                ? "El PDF incluirá automáticamente la columna 'CON IVA' porque este cliente maneja precios con IVA."
                : "El PDF NO incluirá la columna 'CON IVA' porque este cliente maneja precios sin IVA."
              }
            </Alert>
            <Alert severity="success">
              El bono navideño aparecerá automáticamente en noviembre. 
              {shouldShowIVA() && " En la columna 'CON IVA' se incluirá el IVA del bono."}
            </Alert>
          </Paper>

          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={savingCalculation ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSaveCalculation}
                  disabled={savingCalculation || !formData.clienteId}
                  sx={{ minWidth: 200 }}
                >
                  {savingCalculation ? 'Guardando...' : (isEditMode ? 'Actualizar Cálculo' : 'Guardar Cálculo')}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                  onClick={handleGeneratePDF}
                  disabled={generatingPDF || !formData.clienteId}
                  sx={{ minWidth: 200 }}
                >
                  {generatingPDF ? 'Generando PDF...' : 'Generar PDF'}
                </Button>
              </Box>

              {!formData.clienteId && (
                <Alert severity="warning">
                  Debe seleccionar un cliente para poder guardar el cálculo y generar PDF
                </Alert>
              )}

              {savedCalculationId && !isEditMode && (
                <Alert severity="success">
                  Cálculo guardado exitosamente en la base de datos (ID: {savedCalculationId})
                </Alert>
              )}

              {!isEditMode && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Después de guardar o generar PDF, el formulario se limpiará automáticamente
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CalculatorForm;